import classNames from 'classnames';
import uniq from 'lodash/uniq';
import type {
  classNamesThemeOptions,
  flatOption,
  ObjectWithValue,
  optionsDict,
  SelectProps,
  value,
} from './select.types';

const flatOptions = (options: ObjectWithValue[]): flatOption[] => {
  return options.flatMap((option: any) =>
    option.options ? flatOptions(option.options) : option,
  );
};

export const getOptionsDict = (options: ObjectWithValue[] | undefined) => {
  if (!options) return {};
  return flatOptions(options).reduce(
    (acc, option) => Object.assign(acc, { [option.value]: option }),
    {},
  );
};

export const getSelectedOptionsFromDict = ({
  value,
  optionsDict,
  isMulti,
}: {
  value: value;
  optionsDict: optionsDict;
  isMulti?: boolean;
}): flatOption | flatOption[] | null => {
  if (value == null) return null;
  if (isMulti) {
    return uniq(value as [])
      .filter((key) => key != null)
      .map(
        (key: string) =>
          optionsDict[key] || { label: key, value: key, __isCreated: true },
      );
  }
  return (
    optionsDict[value as string] ||
    ({ label: value, value, __isCreated: true } as flatOption)
  );
};

const outerPaddingClasses = {
  xl: { l: 'pl-[1.25rem]', r: 'pr-[1.25rem]', t: 'pt-4.5', b: 'pb-4.5' },
  base: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-3', b: 'pb-3' }, // 'px-2.5 py-3',
  md: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-2.5', b: 'pb-2.5' }, //'p-2.5'
  sm: { l: 'pl-[0.5rem]', r: 'pr-[0.5rem]', t: 'pt-2', b: 'pb-2' }, //'p-2',
  xs: { l: 'pl-[0.375rem]', r: 'pr-[0.375rem]', t: 'pt-1.5', b: 'pb-1.5' }, // 'p-1.5',
};

const ranks = {
  xl: 6,
  base: 4,
  md: 3,
  sm: 2,
  xs: 1,
};

type SizeKey = keyof typeof ranks;
const getSizeKeyUpTo = (max: SizeKey, givin: SizeKey) => {
  const maxRank = ranks[max];
  const givenRank = ranks[givin];
  return maxRank >= givenRank ? givin : max;
};

export const getSelectClassNames = (
  themeOptions?: classNamesThemeOptions,
): SelectProps['classNames'] => {
  const size = themeOptions?.size || 'base';
  const sizeMaxMD = getSizeKeyUpTo('md', size);
  return {
    container: (state) => {
      const common = classNames(
        'rounded-[0.5rem]',
        themeOptions?.containerClassName,
      );

      return common;
    },
    control: (state) => {
      const defaultShadow = 'shadow-[0px_1px_2px_0px_rgba(53,26,77,0.03)]';
      const common = classNames(
        themeOptions?.controlClassName,
        Object.values(outerPaddingClasses[size]).join(' '),
        '!min-h-[auto] rounded-[0.5rem] border select-input-control',
      );

      if (state.isDisabled) {
        return classNames(
          common,
          'bg-primary-50',
          themeOptions?.isError ? 'border-[#EF4444]' : 'border-primary-100',
        );
      }

      if (themeOptions?.isError) {
        return classNames(
          common,
          'bg-white border-[#EF4444]',
          state.isFocused ? 'relative z-[2] ring-2 ring-primary-100' : '',
          themeOptions?.useAIColorScheme ? '' : 'transition',
        );
      }

      if (state.isFocused) {
        return classNames(
          common,
          'bg-white relative z-[2] ring-2 ring-primary-100 border-primary',
          themeOptions?.useAIColorScheme ? '' : 'transition',
        );
      }

      return classNames(
        common,
        defaultShadow,
        themeOptions?.useAIColorScheme
          ? 'ai-input-bg-border'
          : 'transition bg-white hover:bg-primary-50 border-primary-100',
      );
    },
    input: (state) => {
      const common = classNames(themeOptions?.inputClassName);
      if (state.isDisabled) return classNames(common, 'text-primary-200');
      return classNames(common, 'text-primary');
    },
    placeholder: (state) => {
      if (state.isDisabled) return 'text-primary-200';
      return 'text-primary-400';
    },
    valueContainer: (state) => {
      const common = classNames(
        themeOptions?.valueContainerClassName,
        'font-assistant',
        size === 'xl'
          ? 'text-xl md:text-lg font-semibold'
          : 'text-xl md:text-base leading-[1.5rem]',
      );
      return common;
    },
    menu: (state) =>
      classNames(
        outerPaddingClasses.xs.t,
        outerPaddingClasses.xs.b,
        themeOptions?.menuClassName,
        'my-1.5 rounded-[0.5rem] bg-white shadow ring-1 ring-primary-100',
      ),
    menuList: (state) =>
      classNames(
        themeOptions?.menuListClassName,
        'font-assistant text-xl md:text-base text-primary',
      ),
    groupHeading: (state) =>
      classNames(
        Object.values(outerPaddingClasses[sizeMaxMD]).join(' '),
        'font-assistant text-xl md:text-base font-semibold text-primary-700 border-b border-primary-100 leading-[1.5rem]',
      ),
    option: (state) => {
      const common = classNames(
        'transition-colors transition-shadow',
        'leading-[1.5rem]',
        Object.values(outerPaddingClasses[sizeMaxMD]).join(' '),
        themeOptions?.optionClassName,
      );
      if (state.isSelected)
        return classNames(common, 'text-primary-600 bg-primary-50');
      if (state.isDisabled) {
        return classNames(common, 'text-primary-200 cursor-not-allowed');
      }
      if (state.isFocused)
        return classNames(common, 'text-primary-600 bg-primary-50');
      return classNames(common, 'text-primary-600');
    },
  };
};
